'use client';

import { createContext, PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';

import { logger } from '../../../core/Log/logger';
import {
    getUndiscoveredFeatureDiscoveryNotificationsFromFront,
    postFeatureDiscoveryNotificationDiscoveredFromFront,
} from '../../../resources/featureDiscoveryNotification/api/front/featureDiscoveryNotification';
import { FeatureDiscoveryNotificationFromTravelerAPI } from '../../../resources/featureDiscoveryNotification/api/travelerapi/normalizer';
import { FeatureDiscoveryNotification } from '../../../resources/featureDiscoveryNotification/domain/types/FeatureDiscoveryNotification';
import { useLocale } from '../../hooks/useLocale';
import { PickDomain } from '../../utilities/types/PickDomain';

import { useUser } from './useUser';

type FeatureDiscoveryNotificationContextValue = {
    featureDiscoveryNotifications: PickDomain<FeatureDiscoveryNotification, 'name'>[];
    hasFeatureDiscoveryNotification: (name: FeatureDiscoveryNotification['name']) => boolean;
    setFeatureDiscoveryNotificationDiscovered: (
        featureDiscoveryNotification: FeatureDiscoveryNotificationFromTravelerAPI,
    ) => Promise<void>;
};

export const FeatureDiscoveryNotificationContext =
    createContext<FeatureDiscoveryNotificationContextValue>({
        featureDiscoveryNotifications: [],
        hasFeatureDiscoveryNotification: () => false,
        setFeatureDiscoveryNotificationDiscovered: () => Promise.resolve(),
    });

export function FeatureDiscoveryNotificationProvider({ children }: PropsWithChildren) {
    const { user, isUserFetched } = useUser();
    const { locale } = useLocale();
    const [featureDiscoveryNotifications, setFeatureDiscoveryNotifications] = useState<
        PickDomain<FeatureDiscoveryNotification, 'name'>[]
    >([]);

    useEffect(() => {
        if (user !== null && isUserFetched) {
            void (async function fetchNotifications() {
                try {
                    const notifications =
                        await getUndiscoveredFeatureDiscoveryNotificationsFromFront({
                            locale,
                        });
                    setFeatureDiscoveryNotifications(notifications);
                } catch (error) {
                    return error;
                    //logger.error('Error fetching feature discovery notifications:', {}, error); // TODO: uncomment when MEP
                }
            })();
        }
    }, [user, isUserFetched, locale]);

    const hasFeatureDiscoveryNotification = useMemo(
        () => (name: FeatureDiscoveryNotification['name']) => {
            return featureDiscoveryNotifications.some((notification) => notification.name === name);
        },
        [featureDiscoveryNotifications],
    );

    const setFeatureDiscoveryNotificationDiscovered = useCallback(
        async (featureDiscoveryNotification: FeatureDiscoveryNotificationFromTravelerAPI) => {
            setFeatureDiscoveryNotifications((prev) =>
                prev.filter(
                    (notification) =>
                        notification.name !== featureDiscoveryNotification.featureName,
                ),
            );

            try {
                await postFeatureDiscoveryNotificationDiscoveredFromFront({
                    locale,
                    featureDiscoveryNotification,
                });
            } catch (error) {
                logger.error(
                    'Error setting feature discovery notification discovered:',
                    {
                        locale,
                        featureDiscoveryNotification,
                    },
                    error,
                );
            }
        },
        [locale],
    );

    return (
        <FeatureDiscoveryNotificationContext.Provider
            value={{
                featureDiscoveryNotifications,
                hasFeatureDiscoveryNotification,
                setFeatureDiscoveryNotificationDiscovered,
            }}
        >
            {children}
        </FeatureDiscoveryNotificationContext.Provider>
    );
}
